/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useState,
  useEffect,
  useMemo,
  memo,
  useCallback,
  useRef,
} from "react";
import "jspdf-autotable";

import ExcelJS from "exceljs";
import AnalyticsIcon from "@mui/icons-material/QueryStats";
import DescriptionIcon from "@mui/icons-material/Description";
import { saveAs } from "file-saver";
import { useSnackbar } from "notistack";
import {
  Grid,
  Box,
  Card,
  GlobalStyles,
  ToggleButton,
  ToggleButtonGroup,
  Stack
} from "@mui/material";
import AnalyticsReport from "./Analytics";
import { useMaterialUIController } from "context";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import { AutoTooltip } from "utils/Constant";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import GlobalDateFilter from "utils/GlobalDateFilter";
import CustomMaterialTable from "components/custom/CustomMaterialTable";
import Loader from "components/custom/Loader";
import useApiRequest from "utils/Apirequest";
import MDButton from "components/MDButton";
import CustomMaterialTablePage from "../../components/custom/CustomMaterialTablePage";
import PeakLoadGraph from "./peakLoadGraph";
import QueueTimeChart from "./queueTimeChart";
import { Rate } from "antd";
import { convertUTCtoDubai } from "utils/Constant";

const Reports = () => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const [selectedReport] = useState("Summary");
  const [isLoading, setIsLoading] = useState(false);
  const [type] = useState("Sessions");
  const [rows, setRows] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [locationsFetched, setLocationsFetched] = useState(false);

  const [reportType, setReportType] = useState("SUMMARY"); // SUMMARY | PEAK_LOAD | QUEUE
  const [peakData, setPeakData] = useState([]);
  const [peakTotal, setPeakTotal] = useState("");

  const [queueRows, setQueueRows] = useState([]);


  const [isAnalytics, setIsAnalytics] = useState("Analytics");

  const { enqueueSnackbar } = useSnackbar();
  const [controller] = useMaterialUIController();
  const { darkMode, sidenavColor } = controller;

  const [timeRange, setTimeRange] = useState("today");

  const apiRequest = useApiRequest();
  const activeFilterRef = useRef({
    timeRange: "today",
    fromDate: null,
    toDate: null,
  });

  const DEFAULT_FILTER = {
    preset: "today",
    startDate: null,
    endDate: null,
  };

  const [selectedFilter, setSelectedFilter] = useState({
    preset: "today",
    startDate: null,
    endDate: null,
  });

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // reset logic
  }, [reportType]);

  const [allPeakData, setAllPeakData] = useState([]);
  const [allPeakTotal, setAllPeakTotal] = useState("");

  const [driverSummaryRows, setDriverSummaryRows] = useState([]);
  const [driverReviewRows, setDriverReviewRows] = useState([]);
  const [driverReviewColumns, setDriverReviewColumns] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });



  const [page, setPage] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [sorting, setSorting] = useState([
    {
      id: "createdAt",
      desc: true,
    },
  ]);
  const [pageSize, setPageSize] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [columnVisibility, setColumnVisibility] = useState({
    claimed_range: false,
    connector_type: false,
  });
  const [columnFilters, setColumnFilters] = useState([]);

  const handleSortingChange = (newSorting) => {
    setSorting(newSorting);
  };

  const handleSearchChange = (value) => {
    const searchValue =
      typeof value === "string"
        ? value
        : value?.target?.value ?? "";

    setSearchText(searchValue);
    setPage(0);
  };

  useEffect(() => {
    setPage(pagination.pageIndex);
    setPageSize(pagination.pageSize);
  }, [pagination]);


  const fetchReportData = async () => {
    setIsLoading(true);

    let url = `reports/summary?filter=${timeRange}`;

    if (timeRange === "custom" && fromDate && toDate) {
      url += `&start=${fromDate}&end=${toDate}`;
    }

    const { data } =
      await apiRequest("get", url, null, {}, {}, false) || {};

    if (data?.success || data?.status) {
      setRows(data?.data || []);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    if (isAnalytics === "Reports" && reportType === "SUMMARY") {
      fetchReportData();
    }
  }, [fromDate, toDate, timeRange, isAnalytics, reportType]);


  const summaryColumns = useMemo(
    () => [
      {
        header: "Location Name",
        accessorFn: (row) =>
          row?.chargerLocation?.locationName ||
          row?.locationName ||
          row?.location ||
          "-",
        id: "locationName",
        accessorKey: "locationName",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        size: 250,
        Cell: ({ cell }) => {
          const location = cell.getValue() || ' ';
          return <AutoTooltip maxWidth={200}>{location}</AutoTooltip>;
        }
      },
      {
        header: "Station Name",
        accessorFn: (row) => row?.stationName || "-",
        id: "stationName",
        size: 240,
        accessorKey: "stationName",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
      {
        header: "Charge Point ID",
        accessorFn: (row) => row?.chargerId + " - " + row?.connectorId || "-",
        id: "chargePointId",
        accessorKey: "chargePointId",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        size: 260,

      },
      {
        header: "Energy (kWh)",
        accessorFn: (row) => row?.energy || 0,
        id: "energy",
        size: 240,
        accessorKey: "energy",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
      {
        header: "Uptime",
        accessorFn: (row) => row?.uptimeHr || 0,
        id: "uptimeHr",
        size: 220,
        accessorKey: "uptimeHr",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
      {
        header: "Uptime(%)",
        size: 220,
        accessorFn: (row) => row?.uptimePct || 0,
        id: "uptimePct",
        accessorKey: "uptimePct",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
      {
        header: "Downtime",
        accessorFn: (row) =>
          row?.downtime || row?.downtimeHours || row?.downtimeHr || 0,
        id: "downtimeHr",
        size: 240,
        accessorKey: "downtimeHr",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
      {
        header: "Downtime(%)",
        accessorFn: (row) => row?.downtimePct || 0,
        id: "downtimePct",
        size: 260,
        accessorKey: "downtimePct",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
      {
        header: "Charging Duration",
        accessorFn: (row) => row?.chargingDurationHr || 0,
        id: "chargingDurationHr",
        accessorKey: "chargingDurationHr",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        size: 300,
      },
      {
        header: "Charging Utilization(%)",
        accessorFn: (row) => row?.chargingDurationPct || 0,
        id: "chargingDurationPct",
        accessorKey: "chargingDurationPct",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        size: 300,
      },
      {
        header: "Number of Sessions",
        accessorFn: (row) => row?.numberOfSessions || 0,
        id: "numberOfSessions",
        accessorKey: "numberOfSessions",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        size: 300,

      },
      {
        header: "CO2 Saved (kg)",
        accessorFn: (row) => row?.co2Saved || 0,
        id: "co2Saved",
        accessorKey: "co2Saved",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        size: 300,

      },
      {
        header: "Cost (د.إ)",
        accessorFn: (row) => row?.revenue || 0,
        id: "revenue",
        accessorKey: "revenue",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
    ],
    []
  );


  const normalizePreset = (preset) =>
    preset?.toLowerCase().replace(/\s+/g, "_");

  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);

    if (filter?.preset && filter.preset !== "custom") {
      const normalizedPreset = normalizePreset(filter.preset);

      setTimeRange(normalizedPreset);
      setFromDate(null);
      setToDate(null);
    }

    if (filter?.startDate && filter?.endDate) {
      setTimeRange("custom");
      setFromDate(filter.startDate);
      setToDate(filter.endDate);
    }
  };

  const locationOptions = useMemo(() => {
    return [...(locations || [])];
  }, [locations]);

  const fetchLocations = async () => {
    setLoadingLocations(true);

    const { data } = await apiRequest(
      "get",
      "charger-locations",
      null,
      {},
      {},
      false
    ) || {};

    if (data?.status || data?.success) {
      const allOption = {
        _id: "all",
        locationName: "All Locations",
      };

      setLocations([allOption, ...(data.data || [])]);
      setSelectedLocation(allOption); // DEFAULT
      setLocationsFetched(true);
    }

    setLoadingLocations(false);
  };
  useEffect(() => {
    if (isAnalytics !== "Reports") return;
    if (reportType !== "PEAK_LOAD") return;

    if (!locationsFetched) {
      fetchLocations(); // sets "All Locations" by default
    }
  }, [isAnalytics, reportType]);

  const fetchAllPeakLoadData = async () => {
    setIsLoading(true);

    const { data } =
      await apiRequest(
        "get",
        `graph?filter=${timeRange}&start=${fromDate}&end=${toDate}`,
        null,
        {},
        {},
        false
      ) || {};

    if (data?.status) {
      const tableData = data.data.labels.map((label, index) => ({
        date: label,
        energy: Number(data.data.energy[index]?.toFixed(2)),
      }));

      setAllPeakData(tableData);
      setAllPeakTotal(data.data.total?.energy ?? 0);
    }

    setIsLoading(false);
  };

  const fetchPeakLoadData = async () => {
    if (!selectedLocation || selectedLocation._id === "all") return;

    setIsLoading(true);

    const payload = {
      locationId: selectedLocation._id,
      filter: timeRange,
      start: fromDate,
      end: toDate,
    };

    const { data } =
      await apiRequest(
        "post",
        "graph", // CORRECT ROUTE
        payload,
        {},
        {},
        false
      ) || {};

    if (data?.status) {
      const tableData = data.data.labels.map((label, index) => ({
        date: label,
        energy: Number(data.data.energy[index]?.toFixed(2)),
      }));

      setPeakData(tableData);
      setPeakTotal(data.data.total);
    }

    setIsLoading(false);
  };
  useEffect(() => {
    if (isAnalytics !== "Reports") return;
    if (reportType !== "PEAK_LOAD") return;
    if (!selectedLocation) return;

    if (selectedLocation._id === "all") {
      fetchAllPeakLoadData(); // GET /graph
    } else {
      fetchPeakLoadData();    // POST /graph
    }
  }, [
    isAnalytics,
    reportType,
    selectedLocation,
    timeRange,
    fromDate,
    toDate,
  ]);

  const fetchQueueTimeData = async () => {
    setIsLoading(true);

    let url = `reports/wait-time/summary?filter=${timeRange}`;

    if (timeRange === "custom" && fromDate && toDate) {
      url += `&fromDate=${fromDate}&toDate=${toDate}`;
    }

    const { data } =
      await apiRequest("get", url, null, {}, {}, false) || {};

    if (data?.status) {
      setQueueRows(data.data || []);
    }

    setIsLoading(false);
  };

  const fetchDriverSummaryData = async () => {
    setIsLoading(true);

    const payload = {
      filter: timeRange,
      timezone: "UTC",
      currency: "AED",
      ...(timeRange === "custom" && {
        start: fromDate,
        end: toDate,
      }),
    };

    const { data } =
      await apiRequest(
        "post",
        "analytics/report/driver-summary",
        payload,
        {},
        {},
        false
      ) || {};

    if (data?.status) {
      setDriverSummaryRows(data.data || []);
    }

    setIsLoading(false);
  };


  const fetchDriverReviewData = async () => {
    setIsLoading(true);

    const params = new URLSearchParams();
    params.append("filter", timeRange);
    params.append("page", page + 1);
    params.append("limit", pageSize);

    if (searchText) params.append("search", searchText);

    if (timeRange === "custom" && fromDate && toDate) {
      params.append("start", fromDate);
      params.append("end", toDate);
    }

    const { data } = await apiRequest(
      "get",
      `analytics/report/supervisor-ratings?${params.toString()}`,
      null,
      {},
      {},
      false
    ) || {};

    if (data?.status || data?.success) {
      setDriverReviewRows(data.data || []);
      setTotalRows(data.total || 0);
    }

    setIsLoading(false);
  };
  const fetchAllDriverReviewsForExport = async () => {
    const { timeRange, fromDate, toDate } = activeFilterRef.current;

    const params = new URLSearchParams();
    params.append("filter", timeRange);

    if (searchText) params.append("search", searchText);

    if (timeRange === "custom" && fromDate && toDate) {
      params.append("start", fromDate);
      params.append("end", toDate);
    }

    const { data } = await apiRequest(
      "get",
      `analytics/report/supervisor-ratings?${params.toString()}`,
      null,
      {},
      {},
      false
    ) || {};

    return data?.data || [];
  };

  useEffect(() => {
    if (reportType === "DRIVER_REVIEWS") {
      fetchDriverReviewData();
    }
  }, [
    reportType,
    timeRange,
    fromDate,
    toDate,
    page, pageSize, sorting, searchText
  ]);



  useEffect(() => {
    if (reportType === "DRIVER_SUMMARY") {
      fetchDriverSummaryData();
    }
  }, [
    reportType,
    timeRange,
    fromDate,
    toDate,
  ]);


  useEffect(() => {
    if (reportType === "QUEUE_TIME") {
      fetchQueueTimeData();
    }
  }, [reportType, timeRange, fromDate, toDate]);



  useEffect(() => {
    if (isAnalytics !== "Reports") return;
    if (reportType !== "PEAK_LOAD") return;

    if (!selectedLocation) return;

    if (selectedLocation._id === "all") {
      fetchAllPeakLoadData(); // ALL LOCATIONS
    } else {
      fetchPeakLoadData();    // SINGLE LOCATION
    }
  }, [
    reportType,
    selectedLocation,
    timeRange,
    fromDate,
    toDate,
    isAnalytics,
  ]);

  const queueColumns = useMemo(() => [
    {
      header: "Location Name",
      accessorKey: "locationName",
      muiTableHeadCellProps: {
        align: "center",

      },
      muiTableBodyCellProps: {
        align: "center",

      },
      Cell: ({ cell }) => (
        <AutoTooltip maxWidth={220}>
          {cell.getValue() || "-"}
        </AutoTooltip>
      ),
    },
    {
      header: "Average Wait Time (mins)",
      accessorKey: "averageWaitTime",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => cell.getValue() ?? "-",
    },
    {
      header: "Max Wait Time (mins)",
      accessorKey: "maxWaitTime",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => cell.getValue() ?? "-",
    },
    {
      header: "Average Queue Length (Vehicles)",
      accessorKey: "averageQueueLength",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => cell.getValue() ?? "-",
    },
    // {
    //   header: "Throughput (Vehicles / Hour)",
    //   accessorKey: "throughputPerHour",
    //   muiTableHeadCellProps: { align: "center" },
    //   muiTableBodyCellProps: { align: "center" },
    //   Cell: ({ cell }) =>
    //     cell.getValue() !== null
    //       ? Number(cell.getValue()).toFixed(2)
    //       : "-",
    // },
  ], []);

  const driverReviewsColumns = useMemo(() => [
    {
      header: "Driver ID",
      accessorKey: "driverId",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => (
        <AutoTooltip maxWidth={220}>
          {cell.getValue() || "-"}
        </AutoTooltip>
      ),
    },
    {
      header: "Driver Name",
      accessorKey: "driverName",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => cell.getValue() ?? "-",
    },
    {
      header: "Supervisor Name",
      accessorKey: "supervisorName",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => cell.getValue() ?? "-",
    },
    {
      header: "Rating",
      accessorKey: "rating",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ row }) => {
        console.log("Row", row);
        return (
          <div>
            <Rate
              style={{ color: "#f00000" }}
              disabled
              defaultValue={row?.original?.rating}
            />
          </div>
        );
      }
    },
    {
      header: "Comment",
      accessorKey: "comment",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => cell.getValue() ?? "-",
    },
    {
      header: "Rated At",
      accessorKey: "ratedAt",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => {
        return convertUTCtoDubai(cell.getValue());
      },
    },

  ], []);

  useEffect(() => {
    setDriverReviewColumns(driverReviewsColumns);
  }, []);

  const driverColumns = useMemo(() => [
    {
      header: "Driver ID",
      accessorKey: "driverId",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => (
        <AutoTooltip maxWidth={220}>
          {cell.getValue() || "-"}
        </AutoTooltip>
      ),
    },
    {
      header: "Driver Name",
      accessorKey: "driverName",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => cell.getValue() ?? "-",
    },
    {
      header: "Energy Consumed",
      accessorKey: "energyConsumed",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => cell.getValue() ?? "-",
    },
    {
      header: "Charging Sessions",
      accessorKey: "chargingSessions",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => cell.getValue() ?? "-",
    },
    {
      header: "Charging Duration",
      accessorKey: "chargingDuration",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => cell.getValue() ?? "-",
    },
    {
      header: "Average Start SOC",
      accessorKey: "avgStartSoc",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => cell.getValue() ?? "-",
    },
    {
      header: "Average End SOC",
      accessorKey: "avgEndSoc",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => cell.getValue() ?? "-",
    },
    {
      header: "CO2 Saved",
      accessorKey: "co2Saved",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => cell.getValue() ?? "-",
    },
    {
      header: "Leaderboard Points",
      accessorKey: "leaderboardPoints",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => cell.getValue() ?? "-",
    },
    {
      header: "Cost",
      accessorKey: "cost",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => cell.getValue() ?? "-",
    },

  ], []);

  const peakColumns = useMemo(() => [
    {
      header: "Date",
      accessorKey: "date",
      muiTableBodyCellProps: { align: "center" },
      muiTableHeadCellProps: { align: "center" },
    },
    {
      header: "Energy (kWh)",
      accessorKey: "energy",
      muiTableBodyCellProps: { align: "center" },
      muiTableHeadCellProps: { align: "center" },
    },
  ], []);

  const smallToggleStyle = {
    flex: 1,
    border: "none",
    zIndex: 2,
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.9rem",
    whiteSpace: "nowrap",
    padding: "8px 12px",
  };

  const REPORT_TABS = [
    { label: "Summary", value: "SUMMARY" },
    { label: "Peak Load", value: "PEAK_LOAD" },
    { label: "Queue Time", value: "QUEUE_TIME" },
    { label: "Driver Summary", value: "DRIVER_SUMMARY" },
    { label: "Supervisor Reviews", value: "DRIVER_REVIEWS" },
  ];
  const activeIndex = REPORT_TABS.findIndex(
    (tab) => tab.value === reportType
  );


  const exportConfig = useMemo(() => {
    switch (reportType) {
      case "PEAK_LOAD":
        return {
          data:
            selectedLocation?._id === "all"
              ? allPeakData
              : peakData,
          columns: peakColumns,
          sheetName: "Peak Load Report",
        };

      case "QUEUE_TIME":
        return {
          data: queueRows,
          columns: queueColumns,
          sheetName: "Queue Time Report",
        };
      case "DRIVER_SUMMARY":
        return {
          data: driverSummaryRows,
          columns: driverColumns,
          sheetName: "Driver Summary Report",
        };
      case "DRIVER_REVIEWS":
        return {
          data: driverReviewRows,
          columns: driverReviewColumns,
          sheetName: "Supervisor Review Report",
        };

      case "SUMMARY":
      default:
        return {
          data: rows,
          columns: summaryColumns,
          sheetName: "Summary Report",
        };

    }
  }, [
    reportType,
    rows,
    peakData,
    allPeakData,
    queueRows,
    selectedLocation,
    summaryColumns,
    peakColumns,
    queueColumns,
    driverColumns,
    driverSummaryRows,
    driverReviewRows,
    driverReviewColumns,

  ]);

  const exportToExcel = useCallback(async () => {
    try {
      let exportData = [];

      if (reportType === "DRIVER_REVIEWS") {
        exportData = await fetchAllDriverReviewsForExport();
      } else {
        exportData = exportConfig.data;
      }

      if (!exportData || exportData.length === 0) {
        enqueueSnackbar("No data available to export", { variant: "warning" });
        return;
      }

      const { columns, sheetName } = exportConfig;

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(sheetName);

      worksheet.addRow(columns.map(col => col.header));
      worksheet.getRow(1).font = { bold: true };

      exportData.forEach(row => {
        worksheet.addRow(
          columns.map(col =>
            typeof col.accessorFn === "function"
              ? col.accessorFn(row)
              : row[col.accessorKey] ?? "-"
          )
        );
      });

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `${sheetName}_${new Date().toISOString().replace(/[:.-]/g, "_")}.xlsx`
      );
    } catch (error) {
      enqueueSnackbar("Failed to export report", { variant: "error" });
    }
  }, [
    reportType,
    exportConfig,
    enqueueSnackbar,
    timeRange,
    fromDate,
    toDate,
    searchText
  ]);

  useEffect(() => {
    setFilteredData(rows);
  }, [rows]);

  useEffect(() => {
    // reset UI filter
    setSelectedFilter(DEFAULT_FILTER);

    // reset API state
    setTimeRange("today");
    setFromDate(null);
    setToDate(null);

    // reset export/reference state
    activeFilterRef.current = {
      timeRange: "today",
      fromDate: null,
      toDate: null,
    };

    // optional: reset pagination/search (HIGHLY recommended)
    setPage(0);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));

  }, [reportType]);

  return (
    <>
      <GlobalStyles
        styles={{
          /* Remove ALL ToggleButton backgrounds */
          ".MuiToggleButton-root": {
            backgroundColor: "transparent !important",
          },

          ".MuiToggleButton-root:hover": {
            backgroundColor: "transparent !important",
          },

          ".MuiToggleButton-root.Mui-selected": {
            backgroundColor: "transparent !important",
          },

          ".MuiToggleButton-root.Mui-selected:hover": {
            backgroundColor: "transparent !important",
          },

          /* Kill mobile hover media rule */
          "@media (hover: none)": {
            ".MuiToggleButton-root.Mui-selected:hover": {
              backgroundColor: "transparent !important",
            },
          },
        }}
      />

      <DashboardLayout>
        <DashboardNavbar />
        <Box sx={{
          position: "relative", width: "fit-content"
        }}>
          <ToggleButtonGroup
            value={isAnalytics}
            exclusive
            onChange={(e, value) => {
              if (value !== null) setIsAnalytics(value);
            }}
            sx={{
              mb: "1.5rem",
              backgroundColor: darkMode ? "#1f2a37" : "#f4f7f5",
              borderRadius: "14px",
              padding: "4px",
              position: "relative",
              boxShadow: darkMode
                ? "inset 0 0 0 1px rgba(255,255,255,0.08)"
                : "inset 0 0 0 1px rgba(0,0,0,0.08)",
            }}
          >
            {/*  Sliding Gradient Indicator */}
            <Box
              sx={{
                position: "absolute",
                top: 4,
                bottom: 4,
                left: 4,
                width: "calc(50% - 4px)",
                backgroundColor: "#ec4b48",
                borderRadius: "12px",
                transition: "transform 0.35s cubic-bezier(.4,0,.2,1)",
                transform:
                  isAnalytics === "Analytics"
                    ? "translateX(0%)"
                    : "translateX(100%)",
                boxShadow: "0 3px 8px rgba(236,75,72,0.45)",
                zIndex: 1,
              }}
            />


            {/*  ANALYTICS */}
            <ToggleButton
              value="Analytics"
              disableRipple
              sx={{
                flex: 1,
                zIndex: 2,
                border: "none",
                px: 3,
                py: 1.1,
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                color:
                  isAnalytics === "Analytics"
                    ? "#ffffff"
                    : darkMode
                      ? "rgba(255,255,255,0.65)"
                      : "rgba(0,0,0,0.6)",
                transition: "color 0.3s ease, transform 0.2s ease",
                "&:hover": {
                  backgroundColor: "transparent",
                  transform: "scale(1.04)",
                },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <AnalyticsIcon fontSize="small" />
                <span>ANALYTICS</span>
              </Stack>
            </ToggleButton>

            {/*  REPORTS */}
            <ToggleButton
              value="Reports"
              disableRipple
              sx={{
                flex: 1,
                zIndex: 2,
                border: "none",
                px: 3,
                py: 1.1,
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                color:
                  isAnalytics === "Reports"
                    ? "#ffffff"
                    : darkMode
                      ? "rgba(255,255,255,0.65)"
                      : "rgba(0,0,0,0.6)",
                transition: "color 0.3s ease, transform 0.2s ease",
                "&:hover": {
                  backgroundColor: "transparent",
                  transform: "scale(1.04)",
                },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <DescriptionIcon fontSize="small" />
                <span>REPORTS</span>
              </Stack>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {isAnalytics === "Reports" && (
          <Box mb={3}>
            <Grid container spacing={2}>

              {/*  Report Type Toggle */}
              <Grid item xs={12}>
                <ToggleButtonGroup
                  value={reportType}
                  exclusive
                  onChange={(e, value) => value && setReportType(value)}
                  sx={{
                    position: "relative",
                    backgroundColor: darkMode ? "#1f2a37" : "#f4f7f5",
                    borderRadius: "999px",
                    padding: "3px",
                    minWidth: 700,
                    display: "flex",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: 3,
                      bottom: 3,
                      left: 3,
                      width: `calc(${100 / REPORT_TABS.length}% - 3px)`,
                      backgroundColor: "#ec4b48",
                      borderRadius: "999px",
                      transition: "transform 0.35s cubic-bezier(.4,0,.2,1)",
                      transform: `translateX(${activeIndex * 100}%)`,
                      boxShadow: "0 3px 8px rgba(24, 23, 23, 0.45)",
                      zIndex: 1,
                    }}
                  />

                  {REPORT_TABS.map((tab) => (
                    <ToggleButton
                      key={tab.value}
                      value={tab.value}
                      disableRipple
                      sx={smallToggleStyle}
                    >
                      {tab.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>


              </Grid>



            </Grid>
          </Box>
        )}


        {isAnalytics === "Reports" ? (
          <>

            <MDBox mt={8}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={1}
                  px={2}
                  variant="gradient"
                  bgColor={sidenavColor}
                  borderRadius="lg"
                  coloredShadow="info"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <MDTypography variant="h6" color="white">
                    Reports
                  </MDTypography>
                  <MDButton
                    onClick={exportToExcel}
                    disabled={isLoading}
                    variant="outlined"
                    color="white"
                  >
                    Export Reports
                  </MDButton>



                </MDBox>

                <MDBox mt={3} px={{ xs: 1, sm: 2 }}>
                  <Grid container spacing={3}>

                    <Grid item xs={12}>
                      <Grid container spacing={2} alignItems="center" mb={2}>
                        {reportType === "PEAK_LOAD" && (
                          <Grid item xs={12} sm={4} md={3}>
                            <Autocomplete
                              options={locationOptions}
                              loading={loadingLocations}
                              getOptionLabel={(option) => option.locationName || ""}
                              isOptionEqualToValue={(option, value) => option._id === value._id}
                              value={selectedLocation}
                              onChange={(event, newValue) => {
                                setSelectedLocation(newValue);
                              }}
                              onOpen={() => {
                                if (!locationsFetched) fetchLocations();
                              }}
                              sx={{ minWidth: 240, flexGrow: 1 }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Select Location"
                                  size="small"
                                />
                              )}
                            />
                          </Grid>
                        )}


                      </Grid>
                      {isLoading ? (
                        <Loader />
                      ) : (
                        <Box mt={2}>
                          {/*  COMMON DATE FILTER */}
                          <Grid item xs={12} mt={2} mb={2}>
                            <GlobalDateFilter onChange={handleFilterChange} value={selectedFilter} />
                          </Grid>
                          {reportType === "SUMMARY" && (
                            <CustomMaterialTable
                              columns={summaryColumns}
                              data={rows}
                              darkMode={darkMode}
                            />
                          )}

                          {reportType === "PEAK_LOAD" && (
                            <>
                              <MDBox
                                mb={2}
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                flexWrap="wrap"
                                gap={1}
                              >
                                <MDTypography variant="h6">
                                  <strong>
                                    {selectedLocation?._id === "all"
                                      ? "All Locations"
                                      : selectedLocation?.locationName}
                                  </strong>{" "}
                                  – Peak Load (Energy)
                                </MDTypography>

                                <MDTypography variant="h6" fontWeight={600}>
                                  Total Energy:{" "}
                                  {selectedLocation?._id === "all"
                                    ? allPeakTotal
                                    : peakTotal}
                                </MDTypography>
                              </MDBox>

                              <CustomMaterialTable
                                columns={peakColumns}
                                data={
                                  selectedLocation?._id === "all"
                                    ? allPeakData
                                    : peakData
                                }
                                darkMode={darkMode}
                              />

                              {(selectedLocation?._id === "all"
                                ? allPeakData.length > 0
                                : peakData.length > 0) && (
                                  <Box sx={{ height: 340, width: "100%" }}>
                                    <PeakLoadGraph
                                      data={
                                        selectedLocation?._id === "all"
                                          ? allPeakData
                                          : peakData
                                      }
                                    />
                                  </Box>
                                )}
                            </>
                          )}

                          {reportType === "QUEUE_TIME" && (
                            <>
                              <MDTypography variant="h6" mb={2}>
                                Queue Time Report
                              </MDTypography>

                              <CustomMaterialTable
                                columns={queueColumns}
                                data={queueRows}
                                darkMode={darkMode}
                              />

                              {queueRows.length > 0 && (
                                <Box
                                  mt={4}
                                  sx={{
                                    height: 480,
                                    width: "100%",
                                    position: "relative",
                                  }}
                                >
                                  <QueueTimeChart data={queueRows} />
                                </Box>
                              )}

                            </>
                          )}
                          {reportType === "DRIVER_SUMMARY" && (
                            <>
                              <MDTypography variant="h6" mb={2}>
                                Driver Summary Report
                              </MDTypography>

                              <CustomMaterialTable
                                columns={driverColumns}
                                data={driverSummaryRows}
                                darkMode={darkMode}
                              />

                            </>
                          )}
                          {reportType === "DRIVER_REVIEWS" && (
                            <>
                              <MDTypography variant="h6" mb={2}>
                                Driver Reviews
                              </MDTypography>

                              <CustomMaterialTablePage
                                columns={driverReviewColumns}
                                data={driverReviewRows}
                                darkMode={darkMode}
                                manualPagination={true}
                                manualFiltering={false}
                                rowCount={totalRows}
                                paginationChange={(newPage) => setPagination(newPage)}
                                initialState={{
                                  columnVisibility: {
                                    claimed_range: false,
                                    connector_type: false,
                                  },
                                }}
                                soringChange={handleSortingChange}
                                page={page}
                                pagesize={pageSize}
                                paginataion={pagination}
                                sorting={sorting}
                                searchText={searchText}
                                searchChange={handleSearchChange}
                                onColumnFiltersChange={setColumnFilters}
                                onColumnVisibilityChange={setColumnVisibility}
                                state={{
                                  pagination,
                                  sorting,

                                  columnFilters,
                                  columnVisibility,
                                }}
                                globalFilterDebounceMs={2000}
                              />

                            </>
                          )}


                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </MDBox>
              </Card>
            </MDBox>
          </>
        ) : (
          <AnalyticsReport />
        )}
      </DashboardLayout>

      {darkMode && (
        <GlobalStyles
          styles={{
            ".ant-segmented-item-selected": {
              backgroundColor: "#344767 !important",
              color: "#ffffff !important",
              fontWeight: "bold",
            },
            ".ant-segmented-item-selected:hover": {
              backgroundColor: "#344767",
              color: "#ffffff !important",
            },
            ".ant-picker-input input::placeholder": {
              color: "#ffffff !important",
              opacity: 1,
            },
            ".ant-picker-suffix": {
              color: "#ffffff !important",
            },
            ".ant-segmented-thumb": {
              backgroundColor: "#344767 !important",
              transition: "none !important",
            },
          }}
        />
      )}
    </>
  );
};

export default Reports;
